import {
  type AppMarketConfig,
  appMarketConfig as appMarketConfigGeneral,
} from '@smart/configs';
import type {OpenCalculatorListenerV1} from '@smart/payment-config-service';
import {useFeatureAppEnv} from '@smart/react-utils';
import type {Market} from '@smart/types';
import {getAppMarketConfig} from '@smart/utils';
import * as React from 'react';
import {useAppModel} from '../hooks/use-app-model';
import {AppModelContext} from '../hooks/use-app-model-context';
import {useLocale} from '../hooks/use-locale';
import {PurchaseItemContext} from '../hooks/use-purchase-item-context';
import {Calculator} from './calculator';
import {CalculatorSkeleton} from './calculator-skeleton';
import {Dialog} from './dialog';

export interface AppProps {
  done?: () => void;
}

export function App({done}: AppProps): JSX.Element {
  const [appModelResult, fetchAppModel] = useAppModel(done);
  const [openCalculatorListenerArgs, setOpenCalculatorListenerArgs] =
    React.useState<Parameters<OpenCalculatorListenerV1> | undefined>(undefined);
  const {marketId} = useLocale();
  const [appMarketConfig, setAppMarketConfig] = React.useState<AppMarketConfig>(
    getAppMarketConfig(`DEFAULT`, appMarketConfigGeneral),
  );

  const {'payment-config-service': paymentConfigService} =
    useFeatureAppEnv().featureServices;

  const [initialConfig] = React.useState(paymentConfigService?.getConfig());

  React.useEffect(
    () =>
      paymentConfigService?.registerCalculator((...args) => {
        setOpenCalculatorListenerArgs(args);
      }),
    [],
  );

  React.useEffect(() => {
    const newAppMarketConfig = getAppMarketConfig(
      marketId.toUpperCase() as Market,
      appMarketConfigGeneral,
    );

    setAppMarketConfig(newAppMarketConfig);
  }, [marketId]);

  const dialogRef = React.useRef<HTMLDialogElement>(null);

  const closeDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.scrollTop = 0;
      dialogRef.current.close();
    }
    setOpenCalculatorListenerArgs(undefined);
  };

  const onClose = () => {
    const [args, __, onConfigChangeCb] = openCalculatorListenerArgs ?? [];

    if (onConfigChangeCb)
      onConfigChangeCb(
        args?.calculatorConfig ?? initialConfig ?? ({} as any),
        `reset`,
      )?.catch(() => {
        // no
      });
    closeDialog();
  };

  const onApply = () => {
    closeDialog();
  };

  React.useEffect(() => {
    if (openCalculatorListenerArgs && !dialogRef.current?.open) {
      dialogRef.current?.showModal();

      if (
        appModelResult.status === `completed` &&
        `error` in appModelResult.state
      ) {
        fetchAppModel();
      }
    }
  }, [openCalculatorListenerArgs, appModelResult, fetchAppModel]);

  const headerRef = React.useRef<HTMLElement>(null);

  const appModel =
    appModelResult.status === `completed` && `appModel` in appModelResult.state
      ? appModelResult.state.appModel
      : undefined;

  return (
    <Dialog
      ref={dialogRef}
      headline={appModel?.content.headline}
      closeButtonLabel={appModel?.content.closeButtonLabel}
      headerRef={headerRef}
      onClose={onClose}
    >
      {appModel && openCalculatorListenerArgs ? (
        <AppModelContext.Provider value={{appModel, appMarketConfig}}>
          <PurchaseItemContext.Provider
            value={openCalculatorListenerArgs[0].purchaseItem}
          >
            <Calculator
              openCalculatorListenerArgs={openCalculatorListenerArgs}
              headerRef={headerRef}
              onApply={onApply}
            />
          </PurchaseItemContext.Provider>
        </AppModelContext.Provider>
      ) : (
        <CalculatorSkeleton
          status={appModelResult.status}
          onErrorRetry={fetchAppModel}
        />
      )}
    </Dialog>
  );
}
